// src/components/forms/ResetPasswordForm.tsx
import { Form, Input, Button, Tooltip, message, Segmented } from 'antd';
import type { Rule } from 'antd/es/form';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { QuestionCircleOutlined, SafetyCertificateOutlined, MailOutlined, MobileOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { resetPassword, sendResetPasswordCode } from '@/api/auth';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SliderCaptcha } from '@/components/ui/SliderCaptcha';
import type { ResetPasswordRequest } from '@/types/auth';
import type { CaptchaToken } from '@/types/captcha';

/**
重置密码表单组件（支持邮箱/手机 + 滑块验证）
✅ 修复：滑块验证成功后自动获取验证码
✅ 修复：useEffect 依赖警告（使用函数式更新避免读取外部状态）
✅ 修复：TypeScript 严格模式（移除 any，使用 unknown + 类型守卫）
*/
export const ResetPasswordForm = () => {
  const [form] = Form.useForm<ResetPasswordRequest & { confirm_password?: string }>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [sendingCode, setSendingCode] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  
  const [countdowns, setCountdowns] = useState<{ email: number; sms: number }>({
    email: 0,
    sms: 0,
  });
  const [targetVerifiedMap, setTargetVerifiedMap] = useState<{ email: boolean; sms: boolean }>({
    email: false,
    sms: false,
  });
  
  const [cachedTargets, setCachedTargets] = useState<{ email: string; sms: string }>({
    email: '',
    sms: '',
  });

  console.log(cachedTargets);
  
  const isFirstRenderRef = useRef<boolean>(true);
  const [captchaVisible, setCaptchaVisible] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<CaptchaToken | null>(null);
  
  const navigate = useNavigate();
  const currentCountdown = countdowns[channel];
  const currentTargetVerified = targetVerifiedMap[channel];

  // 倒计时逻辑
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdowns(prev => {
        const next = { ...prev };
        if (prev.email > 0) next.email = prev.email - 1;
        if (prev.sms > 0) next.sms = prev.sms - 1;
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 切换渠道时保存/恢复输入值
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    
    const currentTarget = form.getFieldValue('target') as string | undefined;
    const otherChannel: 'email' | 'sms' = channel === 'email' ? 'sms' : 'email';

    setCachedTargets(prev => {
      const updated = {
        ...prev,
        [otherChannel]: currentTarget || '',
      };
      form.setFieldsValue({ 
        target: updated[channel] || '',
        code: ''
      });
      return updated;
    });
  }, [channel, form]);

  const handleChannelChange = useCallback((value: 'email' | 'sms') => {
    if (value === channel) return;
    setChannel(value);
    // ✅ 切换渠道时重置验证码状态
    setTargetVerifiedMap(prev => ({ ...prev, [value]: false }));
  }, [channel]);

  // ✅ 修复 1：提取发送验证码逻辑为独立函数（避免 useCallback 闭包问题）
  const doSendCode = async () => {
    try {
      await form.validateFields(['target']);
      const target = form.getFieldValue('target') as string;
      setSendingCode(true);
      await sendResetPasswordCode({ target, channel });
      message.success(`验证码已发送至您的${channel === 'email' ? '邮箱' : '手机'}`);
      setCountdowns(prev => ({ ...prev, [channel]: 60 }));
      setTargetVerifiedMap(prev => ({ ...prev, [channel]: true }));
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      } else {
        message.error('发送验证码失败');
      }
    } finally {
      setSendingCode(false);
    }
  };

  // ✅ 修复 2：handleSendCode 调用 doSendCode，不依赖 captchaToken 闭包
  const handleSendCode = useCallback(async (): Promise<void> => {
    // 发送验证码前要求滑块验证
    if (!captchaToken?.verified) {
      setCaptchaVisible(true);
      message.warning('请先完成安全验证');
      return;
    }
    // ✅ 验证已通过，直接发送验证码
    await doSendCode();
  }, [captchaToken, channel, form]); // ✅ 依赖数组包含 captchaToken

  // ✅ 修复 3：使用普通函数（不使用 useCallback），确保获取最新 captchaToken
  const handleCaptchaSuccess = (token: CaptchaToken) => {
    setCaptchaToken(token);
    message.success('安全验证通过');
    // ✅ 修复 4：验证成功后自动获取验证码（使用 setTimeout 确保状态更新）
    setTimeout(() => {
      doSendCode();
    }, 300);
  };

  const handleCaptchaClose = useCallback(() => {
    setCaptchaVisible(false);
  }, []);

  const validateConfirmPassword = async (_: unknown, value: unknown): Promise<void> => {
    const password = form.getFieldValue('password') as string | undefined;
    
    if (typeof value === 'string' && typeof password === 'string' && value !== password) {
      return Promise.reject(new Error('两次输入的密码不一致'));
    }
    return Promise.resolve();
  };

  const onFinish = async (values: ResetPasswordRequest): Promise<void> => {
    if (!currentTargetVerified) {
      message.warning(`请先获取${channel === 'email' ? '邮箱' : '手机'}验证码`);
      return;
    }

    const allFields = form.getFieldsValue(true);
    const confirmPassword = allFields.confirm_password as string | undefined;

    if (values.password !== confirmPassword) {
      message.warning('两次输入的密码不一致');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await resetPassword({
        target: values.target,
        channel: values.channel,
        code: values.code,
        password: values.password,
      });
      form.resetFields();
      message.success('密码重置成功，请登录。');
      navigate('/login');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '密码重置失败';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const targetRules: Rule[] = useMemo(() => [
    { required: true, message: `请输入${channel === 'email' ? '邮箱' : '手机号'}` },
    channel === 'email'
      ? { type: 'email', message: '请输入有效的邮箱地址' }
      : { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的中国大陆手机号' },
  ], [channel]);

  return (
    <>
      {error && (
        <ErrorAlert message="重置失败" description={error} />
      )}
      <Form
        form={form}
        name="resetPassword"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        className="space-y-8"
        autoComplete="off"
        initialValues={{ channel: 'email' }}
      >
        <div className="space-y-6">
          <Form.Item
            name="channel"
            label={
              <span className="font-medium text-gray-700">验证方式</span>
            }
            className="mb-0"
          >
            <Segmented
              options={[
                { label: '邮箱验证', value: 'email', icon: <MailOutlined /> },
                { label: '手机验证', value: 'sms', icon: <MobileOutlined /> },
              ]}
              value={channel}
              onChange={handleChannelChange}
              disabled={submitting}
              className="w-full"
            />
          </Form.Item>

          <Form.Item
            name="target"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                {channel === 'email' ? '注册邮箱' : '绑定手机'}
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title={channel === 'email' ? '请输入注册时使用的邮箱' : '请输入绑定的中国大陆手机号'}>
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={targetRules}
            className="mb-0"
          >
            <Input
              placeholder={channel === 'email' ? '请输入注册邮箱' : '请输入绑定的手机号'}
              size="large"
              disabled={submitting || (currentCountdown > 0 && currentTargetVerified)}
              prefix={channel === 'email' ? <MailOutlined className="text-gray-400" /> : <MobileOutlined className="text-gray-400" />}
              className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
            />
          </Form.Item>

          <Form.Item
            name="code"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                {channel === 'email' ? '邮箱验证码' : '短信验证码'}
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title="请输入 6 位验证码">
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={[
              { required: true, message: '请输入验证码' },
              { len: 6, message: '验证码为 6 位数字' },
            ]}
            className="mb-0"
          >
            <div className="flex gap-2">
              <Input
                placeholder="请输入 6 位验证码"
                size="large"
                disabled={submitting || !currentTargetVerified}
                prefix={<SafetyCertificateOutlined className="text-gray-400" />}
                autoComplete="one-time-code"
                className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors flex-1"
              />
              <Button
                type="primary"
                size="large"
                loading={sendingCode}
                disabled={submitting || currentCountdown > 0}
                onClick={handleSendCode}
                className="px-6 bg-linear-to-r from-blue-500 to-purple-600 border-0 hover:from-blue-600 hover:to-purple-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 rounded-lg whitespace-nowrap"
              >
                {currentCountdown > 0 ? `${currentCountdown}s 后重发` : '获取验证码'}
              </Button>
            </div>
          </Form.Item>

          <Form.Item
            name="password"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                新密码
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title="密码至少 6 位，建议字母 + 数字组合。">
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少 6 位' },
            ]}
            className="mb-0"
            hasFeedback
          >
            <Input.Password
              placeholder="请设置新密码（至少 6 位）"
              size="large"
              disabled={submitting}
              autoComplete="new-password"
              className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
            />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                确认新密码
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title="请再次输入新密码，确保一致。">
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={[
              { required: true, message: '请确认新密码' },
              { validator: validateConfirmPassword },
            ]}
            className="mb-0"
            dependencies={['password']}
            hasFeedback
          >
            <Input.Password
              placeholder="请再次输入新密码"
              size="large"
              disabled={submitting}
              autoComplete="new-password"
              className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
            />
          </Form.Item>
        </div>

        <Form.Item className="mb-0 pt-2">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitting}
            disabled={submitting}
            className="w-full px-9 py-2.5 bg-linear-to-r from-blue-500 to-purple-600 border-0 hover:from-blue-600 hover:to-purple-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 rounded-lg"
          >
            {submitting ? '重置中...' : '重置密码'}
          </Button>
        </Form.Item>
      </Form>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
        <p className="text-xs text-blue-700 flex items-start gap-2">
          <SafetyCertificateOutlined className="mt-0.5 shrink-0" />
          <span>
            验证码将发送至您的{channel === 'email' ? '注册邮箱' : '绑定手机'}，有效期 5 分钟。
            如未收到，请检查{channel === 'email' ? '垃圾邮件箱' : '短信拦截'}或 60 秒后重新获取。
          </span>
        </p>
      </div>

      <SliderCaptcha
        visible={captchaVisible}
        scene="reset_password"
        onSuccess={handleCaptchaSuccess}
        onClose={handleCaptchaClose}
      />
    </>
  );
};